import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { FolderIssues, IssueCounts, ObjEntity, PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { ATTACHMENTS } from "../../lib/attach.ts";
import { formatInstant, formatNumber, formatRelative } from "../../lib/format.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { useResource } from "../../lib/resource.ts";
import { SELECTED_ROW } from "../../lib/selection.ts";
import { titleOf } from "../../lib/titles.ts";
import { type Branch, type Group, groupListQuery, groupsOf, useBranch } from "../../lib/tree.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Chevron } from "../../ui/Chevron.tsx";
import { Chip } from "../../ui/Chip.tsx";
import { IssueBadges, ItemCommentCount } from "../../ui/counts.tsx";
import { KIND_ICONS, PaperclipIcon } from "../../ui/icons.tsx";
import { LinkedFromIcon, LinksIcon } from "../../ui/LinkIcons.tsx";
import { Spinner } from "../../ui/Spinner.tsx";
import { LINK } from "../../ui/styles.ts";
import { dragIds } from "../folders/drop.ts";
import { EntityLabel } from "./entityLabel.tsx";
import { PeekKindIcon } from "./icons.tsx";
import { INDENT, SPAN, STEP } from "./treeLayout.ts";

type Row = PageItemRow["data"][number];
/**
 * A group's issues: the server still calls the tree's groups folders (`FolderIssues`, `/items/{id}/folders`), names
 * older than real folders.
 */
type GroupIssues = FolderIssues;

interface TreeProps {
  space: SpaceOut;
  entities: readonly ObjEntity[];
  /** The items above, down to this branch's (left out of it: no loops). */
  path: readonly Row[];
}

type ChildProps = TreeProps & { row: Row; group: Group; issues: GroupIssues | undefined; depth: number };

/**
 * The items in a group, a page at a time (but not the ones above), each a branch of its own. Pages stay as loaded:
 * "Show more" adds the next one below, so nothing above it redraws (nor do open branches close).
 */
function Children(p: ChildProps) {
  // Each page's cursor (the first has none), in order.
  const [pages, setPages] = useState<(string | null)[]>([null]);
  return (
    <>
      {pages.map((cursor, i) => (
        <ChildPage
          key={cursor ?? ""}
          {...p}
          cursor={cursor}
          onMore={i === pages.length - 1 ? (next) => setPages([...pages, next]) : null}
        />
      ))}
    </>
  );
}

/** One page of a group's items; the last offers the next. */
function ChildPage(p: ChildProps & { cursor: string | null; onMore: ((next: string) => void) | null }) {
  const { group } = p;
  const query =
    group.dir === "thread"
      ? { thread: group.thread }
      : group.dir === "out"
        ? { linked_from: `${group.key}:${p.row.id}` }
        : { refers_to: `${group.key}:${p.row.id}`, entity: group.entity ?? "none" };
  // A conversation reads in the order it was written.
  const sort = group.dir === "thread" ? "data.date" : "title";
  const list = useResource(`tree:${p.space.id}:${p.row.id}:${group.id}:${p.cursor ?? ""}`, (signal) =>
    api.get<PageItemRow>(
      spaceApi(p.space, "/items"),
      { ...query, sort, limit: STEP, ...(p.cursor ? { cursor: p.cursor } : {}) },
      signal,
    ),
  );
  const path = [...p.path, p.row];
  const pad = { paddingLeft: `${(p.depth + 1) * INDENT + 0.75}rem` };
  if (list.data === undefined) {
    return (
      <tr>
        <td colSpan={SPAN} class="py-2" style={pad}>
          {list.error ? <span class="text-sm text-red-700">{list.error.message}</span> : <Spinner small={true} />}
        </td>
      </tr>
    );
  }
  const next = list.data.next;
  return (
    <>
      {list.data.data
        .filter((child) => child.id !== p.row.id && !path.some((r) => r.id === child.id))
        .map((child) => (
          <TreeNode key={child.id} {...p} path={path} row={child} depth={p.depth + 1} part={p.issues?.parts ?? false} />
        ))}
      {next && p.onMore ? (
        <tr>
          <td colSpan={SPAN} class="py-1" style={pad}>
            <Button size="xs" variant="ghost" onClick={() => p.onMore?.(next)}>
              {t("items.tree.more")}
            </Button>
          </td>
        </tr>
      ) : null}
    </>
  );
}

const total = (c: IssueCounts) => c.error + c.warning + c.info;

/**
 * A row's issues: with its parts' while it's closed (what it holds, all in one); its own once open, its parts' then
 * shown by group.
 */
export function RowIssues({ row, open }: { row: Row; open: boolean }) {
  const rolled = !open && total(row.with_parts) > total(row.issues);
  return (
    <span title={rolled ? t("items.tree.withParts") : undefined}>
      <IssueBadges counts={open ? row.issues : row.with_parts} />
    </span>
  );
}

/** Which of the server's groups is `f`: its issues, and whether its items are the item's parts. */
const issuesOf = (all: readonly GroupIssues[] | undefined, f: Group): GroupIssues | undefined =>
  all?.find((x) =>
    f.dir === "thread"
      ? x.dir === "thread"
      : x.dir === f.dir && x.key === f.key && (f.dir === "out" || (x.entity ?? null) === f.entity),
  );

/** An item's groups, under its row: each a line with its name, count and issues, the items inside when open. */
export function Subtree(p: TreeProps & { row: Row; branch: Branch; depth: number }) {
  const above = p.path.map((r) => r.id).join(",");
  const issues = useResource(`tree-issues:${p.space.id}:${p.row.id}:${above}`, (signal) =>
    api.get<GroupIssues[]>(spaceApi(p.space, `/items/${p.row.id}/folders`), above ? { above } : {}, signal),
  );
  return (
    <>
      {groupsOf(p.row, p.entities, p.path).map((f) => {
        const open = p.branch.groups.has(f.id);
        return (
          <GroupRows
            key={f.id}
            {...p}
            group={f}
            issues={issuesOf(issues.data, f)}
            open={open}
            onToggle={() => p.branch.toggleGroup(f.id)}
          />
        );
      })}
    </>
  );
}

/** A group's name: its field; or, for the items linking here, their entity and the field they link through. */
function GroupName({ group }: { group: Group }) {
  if (group.dir === "thread") {
    const Glyph = KIND_ICONS.email;
    return (
      <>
        <Glyph class="text-zinc-500" />
        <span class="font-medium">{group.label}</span>
      </>
    );
  }
  if (group.dir === "out") {
    return (
      <>
        {group.key === ATTACHMENTS ? <PaperclipIcon class="text-zinc-500" /> : <LinksIcon class="text-zinc-500" />}
        <span class="font-medium">{group.label}</span>
      </>
    );
  }
  return (
    <>
      <LinkedFromIcon class="text-zinc-500" />
      <span class="font-medium">{group.label}</span>
      {group.field ? <span class="text-zinc-500">{t("item.backrefs.via", { field: group.field })}</span> : null}
    </>
  );
}

function GroupRows(
  p: TreeProps & {
    row: Row;
    group: Group;
    issues: GroupIssues | undefined;
    depth: number;
    open: boolean;
    onToggle: () => void;
  },
) {
  const title = titleOf(p.row);
  const label =
    p.group.dir === "thread"
      ? t("items.tree.threadOf", { title })
      : p.group.dir === "out"
        ? t("items.tree.group", { field: p.group.label, title })
        : p.group.field
          ? t("items.tree.linking", { entity: p.group.label, field: p.group.field, title })
          : t("items.tree.attachedTo", { title });
  const key = p.group.dir === "out" ? p.group.key : null;
  const field = key ? p.entities.find((e) => e.id === p.row.entity)?.fields?.find((f) => f.key === key) : undefined;
  const list = groupListQuery(p.row.id, p.group, field?.target);
  return (
    <>
      <tr class="bg-zinc-50/60">
        <td colSpan={SPAN} class="py-1.5 pr-3" style={{ paddingLeft: `${p.depth * INDENT + 0.75}rem` }}>
          <span class="flex items-center gap-1.5 text-sm text-zinc-700">
            <Chevron open={p.open} label={label} onToggle={p.onToggle} />
            {/* Its name opens its items as a list of their own (when they can be); its chevron folds it. */}
            {list ? (
              <Link
                href={spaceUrl(p.space, list)}
                class="flex items-center gap-1.5 hover:[&>span:first-of-type]:underline"
              >
                <GroupName group={p.group} />
              </Link>
            ) : (
              <button
                type="button"
                class="flex items-center gap-1.5 hover:[&>span:first-of-type]:underline"
                onClick={p.onToggle}
                tabIndex={-1}
              >
                <GroupName group={p.group} />
              </button>
            )}
            <Chip>{formatNumber(p.group.count)}</Chip>
            {/* Only what adds up to the item: its parts' issues (references and its wholes show none here); and only
                closed, as an item's row: open, the items inside show theirs. */}
            {p.issues?.parts && !p.open ? <IssueBadges counts={p.issues.issues} /> : null}
          </span>
        </td>
      </tr>
      {p.open ? <Children {...p} /> : null}
    </>
  );
}

/**
 * An item inside a group (or a folder): kind, title, entity (then what its place adds, `after`), issues, last change;
 * a branch of its own when it links on. `part`: it's a part of the item above (its issues there take in its own
 * parts); else only its own count.
 */
export function TreeNode(
  p: TreeProps & {
    row: Row;
    depth: number;
    part: boolean;
    after?: ComponentChildren;
    /** It can be dragged into a folder (a folder's contents, open in a list). */
    draggable?: boolean;
    /** What starts its line (a folder's contents: a checkbox and its level's indent), in place of the indent alone. */
    lead?: ComponentChildren;
    /** It's selected (drawn so). */
    selected?: boolean;
    /** What dragging it takes (the selection it's in), when not itself alone. */
    dragging?: readonly string[] | undefined;
  },
) {
  const branch = useBranch();
  const { row } = p;
  const entity = p.entities.find((e) => e.id === row.entity);
  const groups = groupsOf(row, p.entities, p.path);
  return (
    <>
      <tr
        class={p.selected ? SELECTED_ROW : undefined}
        draggable={p.draggable}
        onDragStart={
          p.draggable
            ? (e) => (p.dragging ? dragIds(e, p.dragging) : dragIds(e, [row.id], row.folder ?? null))
            : undefined
        }
      >
        <td
          colSpan={SPAN}
          class={`py-1.5 pr-3${p.lead ? " pl-3" : ""}`}
          style={p.lead ? undefined : { paddingLeft: `${p.depth * INDENT + 0.75}rem` }}
        >
          <span class={`flex min-w-0 items-center text-sm ${p.lead ? "gap-1" : "gap-2"}`}>
            {p.lead}
            {groups.length > 0 ? (
              <Chevron
                open={branch.open}
                label={t("items.tree.links", { title: titleOf(row) })}
                onToggle={branch.toggle}
              />
            ) : (
              <span class="size-5 shrink-0" />
            )}
            <PeekKindIcon space={p.space} item={row} />
            <Link href={spaceUrl(p.space, `/items/${row.id}`)} class={`${LINK} min-w-0 truncate`}>
              {titleOf(row)}
            </Link>
            <ItemCommentCount space={p.space} id={row.id} />
            {entity ? <EntityLabel entity={entity} /> : null}
            {p.after}
            {/* Issues by the title, as in the top rows; the date at the far end. */}
            <span class="shrink-0">
              <RowIssues row={row} open={branch.open || !p.part} />
            </span>
            <span class="ml-auto shrink-0 text-xs text-zinc-500" title={formatInstant(row.updated_at)}>
              {formatRelative(row.updated_at)}
            </span>
          </span>
        </td>
      </tr>
      {branch.open ? <Subtree {...p} branch={branch} depth={p.depth + 1} /> : null}
    </>
  );
}
